import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import { useAuthStore } from '@/store/auth.store'

interface AdminGuardProps {
  children: ReactNode
}

/**
 * Guard de admin global — restringe rotas `/admin/*` apenas ao modo de
 * autenticação HTTP Basic.
 *
 * Conforme spec §6 e §7, as páginas administrativas globais (Organizations,
 * Tenants, Workers, Admin Billing) são acessíveis somente pelo admin global,
 * que se autentica via HTTP Basic. Usuários em modo JWT — mesmo com perfil
 * administrativo no seu próprio tenant — não podem acessar essas rotas.
 *
 * Comportamento:
 *   - `auth.mode === 'basic'` → renderiza children
 *   - qualquer outro valor (`'jwt'` ou `null`) → redireciona para `/`
 *
 * Observação: usuários não autenticados (`mode === null`) só chegariam aqui
 * se o `ProtectedRoute` pai falhasse — na prática serão pegos antes e
 * mandados para `/login`. Ainda assim, redirecionar para `/` é seguro: o
 * `ProtectedRoute` interceptará novamente em `/`.
 */
export function AdminGuard({ children }: AdminGuardProps) {
  const mode = useAuthStore((state) => state.mode)

  if (mode !== 'basic') {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
